import { createPortal } from 'react-dom';
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarX, Clock, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './Button';
import { TextArea, TextAreaWrapper } from './TextArea';

/** Antecedência mínima (horas) para o usuário cancelar com devolução do crédito. Espelha o backend. */
export const SESSION_CANCELLATION_MINIMUM_HOURS = 24;
export const CANCEL_REASON_MIN_LENGTH = 3;
export const CANCEL_REASON_MAX_LENGTH = 500;

export type CancelSessionActor = 'user' | 'professional';

type CancelSessionModalProps = {
	isOpen: boolean;
	onClose: () => void;
	/** Chamado com o motivo já validado (3 a 500 caracteres). */
	onConfirm: (reason: string) => void;
	session: Pick<Sessions.Session, 'id' | 'selected_date' | 'payments'> | null;
	actor: CancelSessionActor;
	isPending?: boolean;
	/** Nome exibido no título (ex.: "Maria" ou "Dr. João"). */
	counterpartName?: string;
	zIndex?: number;
};

const sessionUsesClubCredit = (
	session: CancelSessionModalProps['session'],
): boolean => {
	const payments =
		(session?.payments as unknown as Array<{
			payment_benefit_application?: unknown;
		}>) ?? [];
	return payments.some((p) => !!p?.payment_benefit_application);
};

/**
 * Modal único de cancelamento de consulta (usuário e profissional).
 * - Motivo obrigatório para qualquer das partes.
 * - Usuário: mostra se está dentro (>= 24h, crédito devolvido) ou fora
 *   (< 24h, crédito perdido) da política de cancelamento.
 * - Profissional: informa que o paciente será avisado e receberá o crédito de volta.
 */
export const CancelSessionModal = ({
	isOpen,
	onClose,
	onConfirm,
	session,
	actor,
	isPending = false,
	counterpartName,
	zIndex = 10030,
}: CancelSessionModalProps) => {
	const [reason, setReason] = useState('');
	const [touched, setTouched] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setReason('');
			setTouched(false);
		}
	}, [isOpen, session?.id]);

	const policy = useMemo(() => {
		if (!session) return null;
		const sessionDate = new Date(session.selected_date);
		const hoursUntilSession =
			(sessionDate.getTime() - Date.now()) / (1000 * 60 * 60);
		return {
			hoursUntilSession,
			withinPolicy:
				hoursUntilSession >= SESSION_CANCELLATION_MINIMUM_HOURS,
			usesCredit: sessionUsesClubCredit(session),
			formattedDate: `${sessionDate.toLocaleDateString('pt-BR', {
				weekday: 'long',
				day: 'numeric',
				month: 'long',
			})} às ${sessionDate.toLocaleTimeString('pt-BR', {
				hour: '2-digit',
				minute: '2-digit',
			})}`,
		};
	}, [session]);

	if (!isOpen || !session || !policy) return null;

	const trimmed = reason.trim();
	const reasonError =
		trimmed.length < CANCEL_REASON_MIN_LENGTH
			? 'Informe o motivo do cancelamento (mínimo de 3 caracteres).'
			: trimmed.length > CANCEL_REASON_MAX_LENGTH
			  ? `O motivo deve ter no máximo ${CANCEL_REASON_MAX_LENGTH} caracteres.`
			  : null;
	const showError = touched && !!reasonError;
	const isUser = actor === 'user';
	const isLateUserCancel = isUser && !policy.withinPolicy;

	const handleConfirm = () => {
		setTouched(true);
		if (reasonError || isPending) return;
		onConfirm(trimmed);
	};

	const title = isUser
		? 'Cancelar consulta?'
		: counterpartName
		  ? `Cancelar consulta de ${counterpartName}?`
		  : 'Cancelar consulta?';

	return createPortal(
		<div
			className="fixed inset-0 flex items-center justify-center p-4"
			style={{ zIndex }}
			data-testid="cancel-session-modal"
		>
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={isPending ? undefined : onClose}
			/>
			<div className="animate-in zoom-in-95 relative w-full max-w-[440px] rounded-[2rem] bg-white p-7 shadow-2xl duration-200 sm:p-8">
				<div className="mb-4 flex justify-center">
					<div
						className={cn(
							'rounded-full p-4',
							isLateUserCancel
								? 'bg-rose-50 text-rose-500'
								: 'bg-stone-100 text-stone-600',
						)}
					>
						{isLateUserCancel ? (
							<AlertTriangle size={30} />
						) : (
							<CalendarX size={30} />
						)}
					</div>
				</div>

				<h3 className="mb-1 text-center text-xl font-black text-stone-900">
					{title}
				</h3>
				<p className="mb-5 flex items-center justify-center gap-1.5 text-center text-xs font-semibold capitalize text-stone-500">
					<Clock size={13} />
					{policy.formattedDate}
				</p>

				{isUser ? (
					policy.withinPolicy ? (
						<div
							className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-left text-sm leading-relaxed text-emerald-900"
							data-testid="cancel-policy-within"
						>
							<p className="font-bold">
								Cancelamento dentro da política de 24h
							</p>
							<p className="mt-1">
								Você está cancelando com mais de 24 horas de
								antecedência.{' '}
								{policy.usesCredit
									? 'O crédito usado nesta consulta volta para o seu saldo e pode ser usado em um novo agendamento dentro do mês.'
									: 'O valor pago será tratado pela nossa equipe conforme os Termos de Uso.'}
							</p>
						</div>
					) : (
						<div
							className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-left text-sm leading-relaxed text-rose-900"
							data-testid="cancel-policy-late"
						>
							<p className="font-bold">
								Faltam menos de 24 horas para a consulta
							</p>
							<p className="mt-1">
								Por política da empresa, cancelamentos com menos
								de 24 horas de antecedência{' '}
								<strong>não devolvem o crédito</strong>. Ao
								confirmar, você perderá o crédito desta consulta
								e a sessão será considerada realizada.
							</p>
							<p className="mt-2 text-xs text-rose-800">
								Isso precisa ser assim porque o profissional
								reservou este horário exclusivamente para você e
								concordou com essa política ao aceitar o
								agendamento. Está previsto na Cláusula 7 dos
								Termos de Uso.
							</p>
						</div>
					)
				) : (
					<div className="mb-5 rounded-2xl border border-stone-200 bg-stone-50 p-4 text-left text-sm leading-relaxed text-stone-700">
						<p className="flex items-start gap-2">
							<Info size={16} className="mt-0.5 shrink-0" />
							<span>
								O paciente será avisado do cancelamento e, se
								usou crédito do clube, ele será devolvido
								automaticamente. Esta ação não pode ser
								desfeita.
							</span>
						</p>
					</div>
				)}

				<label className="mb-1.5 block text-left text-xs font-bold uppercase tracking-wide text-stone-500">
					Motivo do cancelamento{' '}
					<span className="text-rose-500">*</span>
				</label>
				<TextAreaWrapper
					className={cn(
						'rounded-xl bg-white',
						showError &&
							'border-rose-400 focus-within:ring-rose-400',
					)}
				>
					<TextArea
						data-testid="cancel-session-reason"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						onBlur={() => setTouched(true)}
						maxLength={CANCEL_REASON_MAX_LENGTH}
						placeholder={
							isUser
								? 'Ex.: tive um imprevisto de trabalho nesse horário.'
								: 'Ex.: precisei remarcar por motivo de saúde.'
						}
						disabled={isPending}
						rows={3}
					/>
				</TextAreaWrapper>
				<div className="mt-1 flex items-center justify-between text-[11px]">
					<span
						className={cn(
							showError ? 'text-rose-600' : 'text-transparent',
						)}
					>
						{reasonError ?? '.'}
					</span>
					<span className="text-stone-400">
						{trimmed.length}/{CANCEL_REASON_MAX_LENGTH}
					</span>
				</div>

				<div className="mt-5 flex flex-col gap-3">
					<Button
						type="button"
						data-testid="cancel-session-confirm"
						text={
							isPending
								? 'Cancelando...'
								: isLateUserCancel
								  ? 'Entendi, cancelar e perder o crédito'
								  : 'Confirmar cancelamento'
						}
						className={cn(
							'w-full justify-center rounded-full border-none py-3 font-bold text-white shadow-md disabled:opacity-60',
							isLateUserCancel
								? 'bg-rose-600 hover:bg-rose-700'
								: 'bg-zinc-900 hover:bg-zinc-800',
						)}
						onClick={handleConfirm}
						disabled={isPending || (touched && !!reasonError)}
					/>
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-600 disabled:opacity-50"
					>
						Manter consulta
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};
